import { apiFetch } from "../api/client";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../config";

const REFRESH_INTERVAL = 15000;

function formatDate(value) {
  if (!value) return "Never";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString();
}

function timeAgo(value) {
  if (!value) return "Never";

  const timestamp = new Date(value).getTime();

  if (Number.isNaN(timestamp)) {
    return String(value);
  }

  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));

  if (seconds < 60) return `${seconds}s ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function MetricCard({ label, value, detail, tone = "default" }) {
  return (
    <div className={`soc-wallboard-metric soc-wallboard-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  );
}

function SOCOperationsPage() {
  const navigate = useNavigate();

  const [overview, setOverview] = useState({});
  const [workload, setWorkload] = useState([]);
  const [activity, setActivity] = useState([]);
  const [queue, setQueue] = useState([]);
  const [recommended, setRecommended] = useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadData = useCallback(async (manual = false) => {
    try {
      manual ? setRefreshing(true) : setLoading(true);
      setError("");

      const responses = await Promise.all([
        apiFetch(`/soc/dashboard/overview`),
        apiFetch(`/soc/dashboard/workload`),
        apiFetch(`/soc/dashboard/live-activity`),
        apiFetch(`/soc/dashboard/priority-queue`),
        apiFetch(`/soc/dashboard/recommended-analyst`),
      ]);

      const failed = responses.find((response) => !response.ok);

      if (failed) {
        throw new Error(`SOC API failed with status ${failed.status}`);
      }

      const [
        overviewData,
        workloadData,
        activityData,
        queueData,
        recommendationData,
      ] = await Promise.all(responses.map((response) => response.json()));

      setOverview(overviewData || {});
      setWorkload(Array.isArray(workloadData) ? workloadData : []);
      setActivity(Array.isArray(activityData) ? activityData : []);
      setQueue(Array.isArray(queueData) ? queueData : []);
      setRecommended(recommendationData?.recommended || null);
      setLastRefresh(new Date());
    } catch (loadError) {
      console.error("SOC operations load error:", loadError);
      setError("Unable to load SOC operations data.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();

    const timer = window.setInterval(() => {
      loadData(false);
    }, REFRESH_INTERVAL);

    return () => window.clearInterval(timer);
  }, [loadData]);

  const incidents = overview.incidents || {};
  const analysts = overview.analysts || {};
  const devices = overview.devices || {};

  const topQueue = useMemo(() => queue.slice(0, 8), [queue]);
  const recentActivity = useMemo(() => activity.slice(0, 10), [activity]);

  if (loading) {
    return <div className="soc-wallboard-loading">Loading SOC operations...</div>;
  }

  return (
    <div className="soc-wallboard-page">
      <section className="soc-wallboard-hero">
        <div>
          <div className="soc-wallboard-eyebrow">Wireless Press 24×7 SOC</div>
          <h2>Security Operations Wallboard</h2>
          <p>
            Live incident operations, analyst workload, SLA posture, endpoint
            health, and investigation activity.
          </p>
        </div>

        <div className="soc-wallboard-actions">
          <button type="button" onClick={() => navigate("/incidents")}>
            Open Incident Queue
          </button>

          <button
            type="button"
            onClick={() => loadData(true)}
            disabled={refreshing}
          >
            {refreshing ? "Refreshing..." : "Refresh Wallboard"}
          </button>
        </div>
      </section>

      {error && <div className="soc-wallboard-error">{error}</div>}

      <section className="soc-wallboard-livebar">
        <span>
          <strong>Open:</strong> {incidents.open_incidents || 0}
        </span>
        <span>
          <strong>Available Analysts:</strong> {analysts.available_analysts || 0}
        </span>
        <span>
          <strong>Online Devices:</strong> {devices.online_devices || 0}
        </span>
        <span>
          <strong>SLA:</strong> {overview.sla_compliance_percent || 0}%
        </span>
        <span>
          <strong>Last Refresh:</strong>{" "}
          {lastRefresh ? lastRefresh.toLocaleTimeString() : "Waiting"}
        </span>
      </section>

      <section className="soc-wallboard-metrics">
        <MetricCard
          label="Open Incidents"
          value={incidents.open_incidents || 0}
          detail={`${incidents.unassigned_incidents || 0} unassigned`}
        />

        <MetricCard
          label="P1 Critical"
          value={incidents.p1_open || 0}
          detail="Immediate response"
          tone="danger"
        />

        <MetricCard
          label="P2 High"
          value={incidents.p2_open || 0}
          detail="High priority"
          tone="warning"
        />

        <MetricCard
          label="New Cases"
          value={incidents.new_incidents || 0}
          detail="Awaiting triage"
        />

        <MetricCard
          label="SLA Compliance"
          value={`${overview.sla_compliance_percent || 0}%`}
          detail={`${incidents.acknowledgement_sla_breaches || 0} acknowledgement breaches`}
          tone={
            Number(overview.sla_compliance_percent || 0) >= 90
              ? "success"
              : "warning"
          }
        />

        <MetricCard
          label="Available Analysts"
          value={analysts.available_analysts || 0}
          detail={`${analysts.active_analysts || 0} active`}
          tone="success"
        />

        <MetricCard
          label="Online Endpoints"
          value={devices.online_devices || 0}
          detail={`${devices.offline_devices || 0} offline`}
          tone={
            Number(devices.offline_devices || 0) > 0
              ? "warning"
              : "success"
          }
        />

        <MetricCard
          label="Unprotected Devices"
          value={devices.devices_without_policy || 0}
          detail="Missing policy"
          tone={
            Number(devices.devices_without_policy || 0) > 0
              ? "danger"
              : "success"
          }
        />
      </section>

      <section className="soc-wallboard-main-grid">
        <div className="soc-wallboard-panel">
          <div className="soc-wallboard-panel-heading">
            <div>
              <span>Prioritized response</span>
              <h3>Active Incident Queue</h3>
            </div>

            <button type="button" onClick={() => navigate("/incidents")}>
              View All
            </button>
          </div>

          <div className="soc-wallboard-queue">
            {topQueue.length > 0 ? (
              topQueue.map((incident) => (
                <button
                  key={incident.id}
                  type="button"
                  className="soc-wallboard-queue-item"
                  onClick={() => navigate(`/incidents/${incident.id}`)}
                >
                  <span
                    className={`soc-wallboard-priority ${String(
                      incident.priority || "P3"
                    ).toLowerCase()}`}
                  >
                    {incident.priority || "P3"}
                  </span>

                  <div>
                    <strong>{incident.title}</strong>
                    <span>
                      {incident.incident_number} ·{" "}
                      {incident.company_name || "Unassigned customer"}
                    </span>
                    <small>
                      {incident.device_hostname ||
                        incident.device_uuid ||
                        "No endpoint"}
                    </small>
                  </div>

                  <div className="soc-wallboard-queue-meta">
                    <strong>{incident.status}</strong>
                    <span>{timeAgo(incident.created_at)}</span>
                  </div>
                </button>
              ))
            ) : (
              <div className="soc-wallboard-empty">No active incidents.</div>
            )}
          </div>
        </div>

        <div className="soc-wallboard-panel">
          <div className="soc-wallboard-panel-heading">
            <div>
              <span>Assignment intelligence</span>
              <h3>Recommended Analyst</h3>
            </div>
          </div>

          {recommended ? (
            <div className="soc-wallboard-recommended">
              <div className="soc-wallboard-avatar">
                {recommended.full_name
                  .split(" ")
                  .map((part) => part[0])
                  .join("")
                  .slice(0, 2)}
              </div>

              <h3>{recommended.full_name}</h3>
              <p>
                {recommended.role} · {recommended.tier}
              </p>

              <div className="soc-wallboard-recommended-grid">
                <div>
                  <span>Team</span>
                  <strong>{recommended.team_name}</strong>
                </div>

                <div>
                  <span>Availability</span>
                  <strong>{recommended.availability}</strong>
                </div>

                <div>
                  <span>Workload</span>
                  <strong>
                    {recommended.active_incident_count}/
                    {recommended.max_active_incidents}
                  </strong>
                </div>

                <div>
                  <span>Utilization</span>
                  <strong>{recommended.workload_percent || 0}%</strong>
                </div>
              </div>
            </div>
          ) : (
            <div className="soc-wallboard-empty">
              No analyst is currently available.
            </div>
          )}
        </div>
      </section>

      <section className="soc-wallboard-bottom-grid">
        <div className="soc-wallboard-panel">
          <div className="soc-wallboard-panel-heading">
            <div>
              <span>Team capacity</span>
              <h3>Analyst Workload</h3>
            </div>
          </div>

          <div className="soc-wallboard-workload">
            {workload.length > 0 ? (
              workload.map((analyst) => {
                const width = Math.min(
                  100,
                  Number(analyst.workload_percent || 0)
                );

                return (
                  <div className="soc-wallboard-analyst" key={analyst.id}>
                    <div className="soc-wallboard-analyst-heading">
                      <div>
                        <strong>{analyst.full_name}</strong>
                        <span>
                          {analyst.tier} · {analyst.team_name} ·{" "}
                          {analyst.availability}
                        </span>
                      </div>

                      <strong>
                        {analyst.active_incident_count}/
                        {analyst.max_active_incidents}
                      </strong>
                    </div>

                    <div className="soc-wallboard-progress">
                      <span style={{ width: `${width}%` }} />
                    </div>

                    <small>
                      P1: {analyst.p1_count || 0} · P2:{" "}
                      {analyst.p2_count || 0} · {width}% utilized
                    </small>
                  </div>
                );
              })
            ) : (
              <div className="soc-wallboard-empty">No analysts found.</div>
            )}
          </div>
        </div>

        <div className="soc-wallboard-panel">
          <div className="soc-wallboard-panel-heading">
            <div>
              <span>Operational activity</span>
              <h3>Live Investigation Feed</h3>
            </div>
          </div>

          <div className="soc-wallboard-activity">
            {recentActivity.length > 0 ? (
              recentActivity.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => navigate(`/incidents/${item.incident_id}`)}
                >
                  <div className="soc-wallboard-activity-dot" />

                  <div>
                    <div className="soc-wallboard-activity-heading">
                      <strong>{item.title || item.event_type}</strong>
                      <span>{timeAgo(item.created_at)}</span>
                    </div>

                    <p>{item.description || "SOC activity recorded."}</p>

                    <small>
                      {item.incident_number} ·{" "}
                      {item.actor_name || "Wireless Press SOC"}
                    </small>
                  </div>
                </button>
              ))
            ) : (
              <div className="soc-wallboard-empty">
                No recent investigation activity.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default SOCOperationsPage;
